import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Fires POST /api/analytics/heartbeat on:
 *   - mount (once, when a user is signed in)
 *   - every route change
 *   - every 60 seconds while the tab is visible
 *
 * Server closes the session after a 5-minute idle gap, so pauses are
 * naturally not counted toward engagement time.
 *
 * Sends NO content — just a timestamp so the server can update the
 * user's current session doc. Silent failures on network errors.
 */
export default function AnalyticsHeartbeat() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!user || typeof user !== "object") return undefined;

    const ping = () => {
      // Only ping when the tab is actually visible so background tabs
      // don't inflate engagement time.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      api.post("/analytics/heartbeat").catch(() => {});
    };

    ping();  // fire immediately on user load / route change
    const id = setInterval(ping, 60_000);  // then every 60s while visible
    return () => clearInterval(id);
  }, [user, location.pathname]);

  return null;
}
